"use client"
import { useTranslations } from "next-intl"

import type React from "react"
import { useRef, useCallback, useState, useEffect } from "react"
import { Plus, Package, X, Send, Image, DollarSign } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { cn, formatPrice } from "@/lib/utils"
import { useScheduledCountdown } from "@/hooks/use-scheduled-countdown"
import { parseListingImageUrls } from "@/lib/listings/parse-listing-images"
import { resolveStorageUrl } from "@/lib/storage/public-url"


export interface PhotoAttachment {
  id: string
  file: File
  previewUrl: string
}

export interface ChatComposerListing {
  id: string
  title?: string
  price?: number
  currency?: string
  image_urls?: string[] | string
  scheduled_publish_at?: string | null
  [key: string]: any
}

export interface ChatComposerOrder {
  orderId?: string
  id?: string
  orderNumber?: string
  total?: number
  firstItemImage?: string
  firstItemTitle?: string
  [key: string]: any
}

function ComposerAttachmentListingChip({
  preview,
  title,
  price,
  currency,
  scheduledPublishAt,
  onRemove,
  trapFocusWhenReportOpen,
}: {
  preview: string
  title?: string
  price?: number
  currency?: string | null
  scheduledPublishAt?: string | null
  onRemove: () => void
  trapFocusWhenReportOpen: boolean
}) {
  const { isScheduled } = useScheduledCountdown(scheduledPublishAt ?? null)
  return (
    <div className="relative z-0 shrink-0 w-16 h-16 overflow-visible">
      <div className="relative z-0 w-full h-full rounded-xl overflow-hidden bg-muted dark:bg-neutral-800 border border-border">
        {preview ? (
          <img
            src={preview}
            alt={title || ""}
            className={cn("w-full h-full object-cover", isScheduled && "blur-md brightness-75")}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
        {price != null && (
          <div className="absolute top-1 left-1 bg-black/70 backdrop-blur-sm px-1 py-0.5 rounded">
            <p className="text-[8px] text-white font-semibold">
              {formatPrice(price, currency ?? "USD")}
            </p>
          </div>
        )}
      </div>
      <button
        type="button"
        tabIndex={trapFocusWhenReportOpen ? -1 : undefined}
        onClick={onRemove}
        className="absolute -top-1 -right-1 z-10 w-5 h-5 bg-muted border-2 border-background rounded-full flex items-center justify-center active:scale-90 transition-transform dark:bg-neutral-800"
        aria-label="Remove"
      >
        <X className="w-3 h-3 text-foreground dark:text-white" strokeWidth={2.5} />
      </button>
    </div>
  )
}

export interface ChatComposerProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onPhotoClick: () => void
  onOpenListingPicker: () => void
  onSellClick?: () => void
  sellItemEnabled?: boolean
  selectedImages: PhotoAttachment[]
  onRemoveImage: (id: string) => void
  selectedListings: ChatComposerListing[]
  onRemoveListing: (id: string) => void
  selectedOrder: ChatComposerOrder | null
  onRemoveOrder: () => void
  disabled?: boolean
  maxLength?: number
  onClearError?: () => void
  listingPicker: React.ReactNode
  onFocus?: () => void
  trapFocusWhenReportOpen?: boolean
}

export function ChatComposer({
  value,
  onChange,
  onSend,
  onPhotoClick,
  onOpenListingPicker,
  onSellClick,
  sellItemEnabled = false,
  selectedImages,
  onRemoveImage,
  selectedListings,
  onRemoveListing,
  selectedOrder,
  onRemoveOrder,
  disabled = false,
  maxLength = 2000,
  onClearError,
  listingPicker,
  onFocus,
  trapFocusWhenReportOpen = false,
}: ChatComposerProps) {
  const tChat = useTranslations("chat");
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [isTextareaFocused, setIsTextareaFocused] = useState(false)

  useEffect(() => {
    if (trapFocusWhenReportOpen && textareaRef.current && document.activeElement === textareaRef.current) {
      textareaRef.current.blur()
    }
  }, [trapFocusWhenReportOpen])

  useEffect(() => {
    if (value.trim().length === 0 && textareaRef.current) {
      const el = textareaRef.current
      const prevHeight = el.offsetHeight
      const singleLineHeight = 40
      if (prevHeight <= singleLineHeight) return
      el.style.transition = "height 0.2s ease-out"
      el.style.height = `${prevHeight}px`
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.height = `${singleLineHeight}px`
        })
      })
      const done = () => {
        el.style.transition = ""
        el.style.height = "auto"
        el.removeEventListener("transitionend", done)
      }
      el.addEventListener("transitionend", done)
      return () => el.removeEventListener("transitionend", done)
    }
  }, [value])

  const hasContent =
    value.trim().length > 0 ||
    selectedImages.length > 0 ||
    selectedListings.length > 0 ||
    !!selectedOrder

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onClearError?.()
      onChange(e.target.value)
      e.target.style.height = "auto"
      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`
    },
    [onChange, onClearError]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && hasContent) {
        e.preventDefault()
        onSend()
      }
    },
    [hasContent, onSend]
  )

  const attachments: {
    type: "photo" | "listing" | "order"
    id: string
    preview: string
    title?: string
    price?: number
    currency?: string
    scheduledPublishAt?: string | null
  }[] = [
    ...selectedImages.map((p) => ({
      type: "photo" as const,
      id: p.id,
      preview: p.previewUrl,
      title: p.file.name,
    })),
    ...selectedListings.map((l) => ({
      type: "listing" as const,
      id: l.id,
      preview: parseListingImageUrls(l?.image_urls)[0] || "",
      title: l.title,
      price: l.price,
      currency: l.currency,
      scheduledPublishAt: l.scheduled_publish_at ?? null,
    })),
    ...(selectedOrder
      ? [
          {
            type: "order" as const,
            id: selectedOrder.orderId || selectedOrder.id || "order",
            preview: resolveStorageUrl(selectedOrder.firstItemImage) || "",
            title: selectedOrder.firstItemTitle,
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col w-full">
      {listingPicker}

      {attachments.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] pt-1 pr-1">
          {attachments.map((a) =>
            a.type === "listing" ? (
              <ComposerAttachmentListingChip
                key={a.id}
                preview={a.preview}
                title={a.title}
                price={a.price}
                currency={a.currency}
                scheduledPublishAt={a.scheduledPublishAt}
                trapFocusWhenReportOpen={trapFocusWhenReportOpen}
                onRemove={() => onRemoveListing(a.id)}
              />
            ) : (
              <div key={a.id} className="relative z-0 shrink-0 w-16 h-16 overflow-visible">
                <div className="relative z-0 w-full h-full rounded-xl overflow-hidden bg-muted dark:bg-neutral-800 border border-border">
                  {a.preview ? (
                    <img src={a.preview} alt={a.title || ""} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  {a.price != null && (
                    <div className="absolute top-1 left-1 bg-black/70 backdrop-blur-sm px-1 py-0.5 rounded">
                      <p className="text-[8px] text-white font-semibold">
                        {formatPrice(a.price, a.currency ?? "USD")}
                      </p>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  tabIndex={trapFocusWhenReportOpen ? -1 : undefined}
                  onClick={() => {
                    if (a.type === "photo") onRemoveImage(a.id)
                    if (a.type === "order") onRemoveOrder()
                  }}
                  className="absolute -top-1 -right-1 z-10 w-5 h-5 bg-muted border-2 border-background rounded-full flex items-center justify-center active:scale-90 transition-transform dark:bg-neutral-800"
                  aria-label="Remove"
                >
                  <X className="w-3 h-3 text-foreground dark:text-white" strokeWidth={2.5} />
                </button>
              </div>
            ),
          )}
        </div>
      )}

      {value.length >= (maxLength ?? 2000) && (
        <p className="text-xs text-destructive mb-2">You reached the {maxLength}-character limit.</p>
      )}

      <div className={cn(
        "flex items-center bg-muted dark:bg-neutral-800 rounded-3xl min-h-[44px] py-1 pl-1 pr-2 gap-1",
        isTextareaFocused && "ring-2 ring-ring ring-offset-2 ring-offset-background"
      )}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              tabIndex={trapFocusWhenReportOpen ? -1 : undefined}
              className="h-9 w-9 p-0 rounded-full shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent dark:text-neutral-400 dark:hover:text-white dark:hover:bg-neutral-700"
              disabled={disabled}
              aria-label={tChat("addAttachment")}
            >
              <Plus className="h-5 w-5" strokeWidth={2.5} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" surface="chrome" className="z-10000">
            <DropdownMenuItem onClick={onPhotoClick}>
              <Image className="h-4 w-4 mr-2" />
              {tChat("photo")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onOpenListingPicker}>
              <Package className="h-4 w-4 mr-2" />
              {tChat("listing")}
            </DropdownMenuItem>
            {sellItemEnabled && onSellClick ? (
              <DropdownMenuItem onClick={onSellClick}>
                <DollarSign className="h-4 w-4 mr-2" />
                {tChat("sellAnItem")}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={tChat("message")}
          rows={1}
          disabled={disabled}
          maxLength={maxLength}
          tabIndex={trapFocusWhenReportOpen ? -1 : undefined}
          onFocus={() => { setIsTextareaFocused(true); onFocus?.() }}
          onBlur={() => setIsTextareaFocused(false)}
          className="flex-1 min-w-0 py-2.5 px-1 bg-transparent text-foreground placeholder:text-muted-foreground text-base outline-none resize-none leading-5 max-h-[120px] overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none]"
        />

        <AnimatePresence mode="wait">
          {hasContent ? (
            <motion.div
              key="send"
              initial={{ opacity: 0, scale: 0.5, width: 0 }}
              animate={{ opacity: 1, scale: 1, width: 36 }}
              exit={{ opacity: 0, scale: 0.5, width: 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              className="shrink-0 overflow-hidden flex items-center justify-center"
            >
              <Button
                onClick={onSend}
                size="sm"
                tabIndex={trapFocusWhenReportOpen ? -1 : undefined}
                className="h-9 w-9 p-0 rounded-full shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-90 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
                disabled={disabled}
                aria-label={tChat("sendMessage")}
              >
                <Send className="h-[18px] w-[18px]" />
              </Button>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  )
}
